/**
 * The posthog-js singleton's configuration and initialization.
 *
 * `initPostHog()` runs at module scope in `main.tsx`, BEFORE React renders,
 * rather than inside `PostHogProvider`'s mount effect. That is what puts
 * exception autocapture's global handlers in place for auth-SDK hydration and
 * the BootScreen — everything before the router mounts, which a provider-owned
 * init left unreported. `__root.tsx` hands the same singleton to
 * `PostHogProvider` via `client`, so `usePostHog()` returns this instance.
 */
import posthog, { type PostHogConfig } from 'posthog-js';
import { AGENT_BASE, API_BASE } from '@/config';
import { errorTrackingEnabled } from '@/lib/errorTracking';

export const posthogOptions: Partial<PostHogConfig> = {
  // Per-environment, set in `.env.<mode>`. The value is a PostHog-managed
  // reverse proxy: it only forwards to PostHog Cloud, and the `phc_` token in
  // each request decides the project, so a shared host never mixes projects.
  // What a shared host would couple is DNS, which is why each deployed env has
  // its own record (`f.dev`, `f.preprod`, `f` under `khorium.ai`).
  // Hence the fallback is PostHog Cloud direct rather than any `khorium.ai`
  // host: an env that forgets to set this must lose ad-blocker evasion, not
  // silently inherit another environment's domain.
  api_host:
    import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
  // Ingestion may be reverse-proxied; toolbar authentication must still reach
  // the PostHog Cloud application directly.
  ui_host: 'https://us.posthog.com',
  defaults: '2026-01-30',
  person_profiles: 'identified_only',
  capture_pageview: false, // Manual pageview capture via usePostHogPageview
  capture_pageleave: true,
  // Exception autocapture: PostHog's global `error` and `unhandledrejection`
  // handlers, which replaced Sentry's when it was removed. The mechanism for
  // anything nobody wrapped in a try/catch — explicit reports go through
  // `@/lib/errorTracking`.
  //
  // Pinned here rather than left to the project's remote toggle, because a
  // pinned value overrides it (KhoriumAdmin `terraform/posthog`), and code is
  // where a reviewer can see what is captured. Console errors stay off: the
  // app's own errors go through `logger.error`, which already reaches PostHog
  // as a log, so what `console.error` would add is third-party noise billed as
  // `$exception`s.
  //
  // Cost control: PostHog bills per `$exception` (100k/month free). The SDK
  // rate-limits autocaptured exceptions per type, per tab — ten, then one per
  // ten seconds — so one hot error in a loop cannot run away; the exposure is
  // breadth across users. Watch the volume in Error Tracking before widening
  // this.
  //
  // Off in local `pnpm dev:local` (mode `development`) — see `errorTrackingEnabled`.
  capture_exceptions: errorTrackingEnabled()
    ? {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false,
      }
    : false,
  // Web vitals (LCP, CLS, FCP, INP), which Sentry's `browserTracingIntegration`
  // collected until its removal. Pinned so they do not hinge on the project's
  // remote toggle; `network_timing` is left unset, so replay's network timing
  // still follows the remote config.
  capture_performance: { web_vitals: true },
  autocapture: { capture_copied_text: true },
  capture_dead_clicks: true,
  enable_heatmaps: true,
  // Exact-hostname allow-list for the X-POSTHOG-* tracing headers; CORS coupling in docs/posthog-analytics.md "Tracing headers".
  tracing_headers: [API_BASE, AGENT_BASE].map(
    (b) => new URL(b, window.location.origin).hostname,
  ),
  session_recording: {
    maskAllInputs: false,
    maskInputFn: (text, element) => {
      if (element?.getAttribute('type') === 'password')
        return '*'.repeat(text.length);
      return text;
    },
    // Source of truth for capturing the WebGPU mesh-viewer <canvas> in replays.
    // PostHog also gates canvas server-side (Replay settings → `record_canvas`) —
    // keep that toggle on so code and server agree. 8 fps / "0.4" trades
    // smoothness for payload size; canvas frames can't be masked, keep PII off.
    captureCanvas: {
      recordCanvas: true,
      canvasFps: 8,
      canvasQuality: '0.4',
    },
  },
};

/**
 * Absent in the builds that ship no analytics — the hosted/per-PR demo
 * (.env.demo) and the per-PR app preview (.env.pr-preview) — so the SDK is
 * never initialized there and stays dormant.
 */
export const POSTHOG_API_KEY: string | undefined = import.meta.env
  .VITE_PUBLIC_POSTHOG_PROJECT_TOKEN;

/** Initialize the singleton once; a no-op without a project token. */
export function initPostHog() {
  if (!POSTHOG_API_KEY || posthog.__loaded) return;
  posthog.init(POSTHOG_API_KEY, posthogOptions);
}
